/**
 * EmptyState — the customer-engaging "you don't have any X yet, here's
 * how to start" pattern. Replaces the half-dozen ad-hoc inline empty
 * blocks across the entry-point list pages (Apps, Tests, Hints,
 * Reference Docs, Variants, Suites) so the copy + visual shape are
 * consistent — and so adding a new list page doesn't reinvent the
 * panel layout. #1218 Group E.
 *
 * Prior art across the codebase (varied):
 *   - Applications.tsx had a Card-wrapped centered block with icon +
 *     title + body + button. Closest to the target shape.
 *   - Tests/index.tsx had a Card-wrapped block with icon + body but
 *     no CTA (the "no app selected" entry-barrier case).
 *   - Hints.tsx, Results.tsx, ReferenceDocsSection.tsx had their own
 *     variations on the same idea. Each drifted on padding, icon
 *     size, and CTA placement.
 *
 * This component pins one shape so all six pages render identically.
 *
 * Slots:
 *   - `icon` — optional lucide icon component (rendered at 48 px to
 *     match the existing Applications shape). When omitted, the body
 *     gains a hair more breathing room.
 *   - `title` — required short headline ("No apps yet").
 *   - `body` — required prose explaining why this is empty and what
 *     creating one unlocks ("Create your first app to start authoring
 *     tests against it.").
 *   - `action` — optional CTA. `to` is a router path (router-link);
 *     `onClick` is a button. Pick whichever the call site needs — the
 *     prop union enforces exactly one.
 *
 * Container is intentionally a `<Card>` so callers can drop the
 * component directly into a page section without an outer wrapper,
 * matching the prior Applications.tsx shape. If a call site needs the
 * naked block (no card border), it can wrap differently — but the
 * default is the card-bordered shape every existing call site already
 * uses.
 */
import { Link } from 'react-router-dom'
import type { LucideIcon } from 'lucide-react'
// Direct imports rather than the `@/components/ui` barrel — this file
// IS re-exported by the barrel, and pulling siblings through it creates
// a self-loop (`index.ts` → `emptyState.tsx` → `index.ts`) that hurts
// HMR and tree-shaking. The other primitives (`button.tsx`, `card.tsx`)
// follow the same direct-import convention for the same reason.
import { Button } from './button'
import { Card, CardContent } from './card'

type EmptyStateAction =
  | { label: string; onClick: () => void; to?: never }
  | { label: string; to: string; onClick?: never }

interface EmptyStateProps {
  title: string
  body: string
  /** Optional lucide icon component (e.g. `Globe`, `Layers`). Rendered
   * at 48 px in `text-muted-foreground`. Omit for a tighter block. */
  icon?: LucideIcon
  /** Optional CTA. Use `onClick` for a callback (open a dialog, etc.)
   * or `to` for in-app navigation via react-router. */
  action?: EmptyStateAction
}

export function EmptyState({ title, body, icon: Icon, action }: EmptyStateProps) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-12 text-center">
        {Icon && <Icon className="h-12 w-12 text-muted-foreground mb-4" aria-hidden="true" />}
        <h3 className="text-lg font-semibold mb-2">{title}</h3>
        <p className="text-sm text-muted-foreground mb-4 max-w-md">{body}</p>
        {action && (
          action.to ? (
            <Button asChild>
              <Link to={action.to}>{action.label}</Link>
            </Button>
          ) : (
            <Button onClick={action.onClick}>{action.label}</Button>
          )
        )}
      </CardContent>
    </Card>
  )
}
